function isPartnerFlagEnabled(value) {
  return value === true || value === 'true' || value === 1 || value === '1';
}

export { isPartnerFlagEnabled };

function findPartnerKey(partner, logicalKey) {
  if (!partner || typeof partner !== 'object' || Array.isArray(partner)) {
    return null;
  }
  return Object.keys(partner).find((k) => k.toLowerCase() === String(logicalKey).toLowerCase()) || null;
}

export function findPartnerFieldKey(partner, logicalKey) {
  return findPartnerKey(partner, logicalKey);
}

export function isPrerollEnabled(partner) {
  const prerollKey = findPartnerKey(partner, 'prerollEnabled');
  return prerollKey != null && isPartnerFlagEnabled(partner[prerollKey]);
}

/** CTA switch is locked (cannot be turned off) while preroll is enabled. */
export function isCtaSwitchLocked(partner) {
  return isPrerollEnabled(partner);
}

export function isCtaFieldKey(fieldKey) {
  return fieldKey != null && String(fieldKey).toLowerCase() === 'ctaenabled';
}

/** When preroll is on, ensure `ctaEnabled` is true (returns a shallow copy). */
export function syncCtaEnabledWithPreroll(partner) {
  const c = partner && typeof partner === 'object' && !Array.isArray(partner) ? { ...partner } : {};
  if (!isPrerollEnabled(c)) {
    return c;
  }
  const ctaKey = findPartnerKey(c, 'ctaEnabled') || 'ctaEnabled';
  if (!isPartnerFlagEnabled(c[ctaKey])) {
    c[ctaKey] = true;
  }
  return c;
}

/** Apply CTA/preroll rules before POST (preroll on or null CTA → `ctaEnabled: true`). */
export function normalizePartnerCtaForSave(partner) {
  const c = partner && typeof partner === 'object' && !Array.isArray(partner) ? { ...partner } : {};
  const ctaKey = findPartnerKey(c, 'ctaEnabled') || 'ctaEnabled';
  c[ctaKey] = resolveCtaEnabledValue(c);
  return c;
}

/** `ctaEnabled` defaults to true when absent/null; also true when preroll is enabled. */
export function resolveCtaEnabledValue(partner) {
  const ctaKey = findPartnerKey(partner, 'ctaEnabled');
  const prerollKey = findPartnerKey(partner, 'prerollEnabled');
  const ctaVal = ctaKey ? partner[ctaKey] : undefined;
  const prerollOn = prerollKey != null && isPartnerFlagEnabled(partner[prerollKey]);

  if (ctaVal == null) {
    return true;
  }
  if (prerollOn) {
    return true;
  }
  return isPartnerFlagEnabled(ctaVal);
}

/** If `endDisplayAdEnabled` / `ctaDisplayAdEnabled` is absent (any casing), default to true. */
export function ensurePartnerDefaults(partner) {
  const c = partner && typeof partner === 'object' && !Array.isArray(partner) ? { ...partner } : {};
  const keysLower = new Set(Object.keys(c).map((k) => k.toLowerCase()));
  if (keysLower.has('defaultlocale')) {
    const k = Object.keys(c).find((x) => x.toLowerCase() === 'defaultlocale');
    const v = k ? c[k] : undefined;
    if (v == null || String(v).trim() === '') {
      c[k || 'defaultLocale'] = 'en';
    }
  } else {
    c.defaultLocale = 'en';
  }
  if (!keysLower.has('enddisplayadenabled')) {
    c.endDisplayAdEnabled = true;
  }
  if (!keysLower.has('ctadisplayadenabled')) {
    c.ctaDisplayAdEnabled = true;
  }
  if (!keysLower.has('arkadiumcanonicalenabled')) {
    c.arkadiumCanonicalEnabled = true;
  }
  const ctaKey = findPartnerKey(c, 'ctaEnabled') || 'ctaEnabled';
  c[ctaKey] = resolveCtaEnabledValue(c);
  return c;
}

/** Normalizes GET /SyndicationConfig body to a partner object array (same rules as SyndicationConfigPage). */
export function normalizeSyndicationClientsBody(body) {
  if (body == null || body === '') {
    return [];
  }
  if (Array.isArray(body)) {
    return body.map((c) => {
      const base = c && typeof c === 'object' && !Array.isArray(c) ? { ...c } : {};
      return ensurePartnerDefaults(base);
    });
  }
  if (typeof body === 'object') {
    const prefer = ['clients', 'syndicationClients', 'SyndicationClients'];
    for (let i = 0; i < prefer.length; i += 1) {
      const k = prefer[i];
      if (Array.isArray(body[k])) {
        return normalizeSyndicationClientsBody(body[k]);
      }
    }
    const dyn = Object.keys(body).find((k) => Array.isArray(body[k]));
    if (dyn) {
      return normalizeSyndicationClientsBody(body[dyn]);
    }
  }
  return [];
}
